/**
 * Song Requests Dashboard Widget - the read-only glance (R25 chat 2, R6).
 *
 * What is playing (and who asked), how long the queue is, what is next. The
 * queue's actions live on the channel page's panel (`QueuePanel.tsx`), which
 * this links to. One read: the module's `status` route, every 10s.
 */

import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowRight, ListMusic, Music } from 'lucide-react'
import { apiErrorMessage } from '@/api/client'
import type { ModuleWidgetProps } from '../types'
import { playerOfflineText, songRequestsApi, type SongRequestStatus } from './api'

const POLL_MS = 10000

export function SongRequestsWidget({ channelId }: ModuleWidgetProps) {
  const [data, setData] = useState<SongRequestStatus | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)

  useEffect(() => {
    if (!channelId) return
    let alive = true
    async function fetchStatus(id: number) {
      try {
        const status = await songRequestsApi.status(id)
        if (alive) {
          setData(status)
          setLoadError(null)
        }
      } catch (err) {
        if (alive) setLoadError(apiErrorMessage(err, 'Could not load song requests'))
      }
    }
    setData(null)
    fetchStatus(channelId)
    const interval = setInterval(() => fetchStatus(channelId), POLL_MS)
    return () => {
      alive = false
      clearInterval(interval)
    }
  }, [channelId])

  const player = data?.player
  const playing = player?.connected && player.title ? player : null
  const next = data?.queue[0]

  return (
    <div className="card card-pad-sm">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-text-primary flex items-center gap-2">
          <Music size={18} className="text-synth-mint" />
          Song Requests
        </h3>
        {data && (
          <span className={player?.connected ? 'badge-success' : 'badge-neutral'}>
            {player?.connected ? (playing?.state === 'paused' ? 'Paused' : 'Player on') : 'Player off'}
          </span>
        )}
      </div>

      {!channelId ? (
        <p className="text-sm text-text-muted">Pick a channel to see its requests.</p>
      ) : !data ? (
        loadError ? (
          <p className="text-sm text-state-danger">{loadError}</p>
        ) : (
          <div className="h-16 rounded bg-surface-base animate-pulse" />
        )
      ) : (
        <div className="space-y-3">
          <div className="min-w-0">
            {!player?.connected ? (
              <p className="text-sm text-text-muted">{playerOfflineText(player?.reason ?? null)}</p>
            ) : playing ? (
              <>
                <p className="text-sm font-medium text-text-primary truncate">{playing.title}</p>
                <p className="text-xs text-text-muted truncate">
                  {playing.artist}
                  {playing.requested_by ? ` · requested by @${playing.requested_by}` : ''}
                </p>
              </>
            ) : (
              <p className="text-sm text-text-muted">Nothing is playing</p>
            )}
          </div>

          <div className="divider" />

          <div className="flex items-center justify-between gap-3 text-sm">
            <span className="flex items-center gap-2 text-text-secondary shrink-0">
              <ListMusic size={16} />
              {data.queue_length} queued
            </span>
            {next && (
              <span className="text-xs text-text-muted truncate">Next: {next.song.title}</span>
            )}
          </div>

          <Link
            to={`/channels/${channelId}`}
            className="text-xs text-synth-cyan hover:underline flex items-center gap-1"
          >
            Manage the queue <ArrowRight size={12} />
          </Link>
        </div>
      )}
    </div>
  )
}

export default SongRequestsWidget
